'use client';

import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { SettingsEditDialog } from '../settings/SettingsEditDialog';
import {
  isApplicationType,
  type ApplicationType,
} from './application-general.model';
import { SettingsSelectField } from './SettingsSelectField';
import { useApplicationTypeSelectCollection } from './useApplicationGeneralCollections';
import { useDraftOnOpen } from './useDraftOnOpen';

export type ApplicationTypeEditDialogProps = {
  open: boolean;
  applicationType: ApplicationType;
  loading?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (applicationType: ApplicationType) => Promise<boolean>;
  isSubmitDisabled?: (draft: ApplicationType) => boolean;
  renderDraftDetails?: (draft: ApplicationType) => ReactNode;
};

export function ApplicationTypeEditDialog({
  open,
  applicationType,
  loading,
  onOpenChange,
  onSubmit,
  isSubmitDisabled,
  renderDraftDetails,
}: ApplicationTypeEditDialogProps) {
  const t = useTranslations('designSystem.applicationGeneral');
  const tCommon = useTranslations('designSystem.common');
  const collection = useApplicationTypeSelectCollection();
  const [draft, setDraft] = useDraftOnOpen({ open, value: applicationType });

  return (
    <SettingsEditDialog
      open={open}
      unmountOnExit
      title={t('editApplicationType')}
      onOpenChange={onOpenChange}
      onSubmit={async () => {
        if (await onSubmit(draft)) {
          onOpenChange(false);
        }
      }}
      loading={loading}
      isSubmitDisabled={
        isSubmitDisabled ? isSubmitDisabled(draft) : draft === applicationType
      }
      submitText={tCommon('saveChanges')}
    >
      <VStack alignItems="stretch" gap={6} w="full">
        <SettingsSelectField
          label={t('applicationType')}
          collection={collection}
          value={draft}
          onValueChange={(value) => {
            if (isApplicationType(value)) {
              setDraft(value);
            }
          }}
        />
        {renderDraftDetails?.(draft)}
      </VStack>
    </SettingsEditDialog>
  );
}
